import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { BellRing, CalendarDays, CalendarOff, CalendarPlus, CalendarRange, Check, ChevronLeft, ChevronRight, Copy, Lock, Printer, Settings, Users } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { isAdmin as isAdminRole, isDoctor, canForceSlot, tieneAgente } from '../lib/roles';
import {
  listAgendas,
  listConceptos,
  conceptosElegibles,
  listCitas,
  listBloqueos,
  todayStr,
  addDaysStr,
  strToDate,
  dateToStr,
  esFechaValida,
  diaEnFrase,
  citasDeLaColumna,
  ordenarAgendasPorContenido,
  columnasDeTodas,
  seVeEnTodasSinCitas,
  agendaTieneCitas,
  recuentoDelDia,
  pacientesCitados,
  medicosVisitantesDelDia,
  resumenDelMes,
} from '../lib/citasApi';
import { conActividadEnElMes, primeroDelMes, sumarMeses, textoMes } from '../lib/agendaMes';
import { PageHeader, EmptyState } from '../components/Common';
import SelectorAgendas from '../components/citas/SelectorAgendas';
import NumeroPacientes from '../components/citas/NumeroPacientes';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Skeleton } from '../components/ui/skeleton';
import AgendaGrid from '../components/citas/AgendaGrid';
import AgendaMes from '../components/citas/AgendaMes';
import QuirofanoDiaBar from '../components/citas/QuirofanoDiaBar';
import { MedicoVisitanteBarra, VisitantesColumna } from '../components/citas/MedicoVisitanteDia';
import LeyendaEquipo from '../components/citas/LeyendaEquipo';
import EstadoAgendaColumna, { ESTADOS_CON_LINEA } from '../components/citas/EstadoAgendaColumna';
import { estadoDeAgendasDelDia } from '../lib/disponibilidad';
import CitaCreateDialog from '../components/citas/CitaCreateDialog';
import CitaDetailPopover from '../components/citas/CitaDetailPopover';
import { conservarIntervenciones } from '../components/citas/EtiquetasIntervenciones';
import BloqueoDialog from '../components/citas/BloqueoDialog';
import AgenteAskDialog from '../components/AgenteAskDialog';
import { useClinica } from '../lib/clinica';
import RecordatoriosDialog from '../components/citas/RecordatoriosDialog';
import MicButton from '../components/MicButton';
import AgenteIcono from '../components/AgenteIcono';
import AgendaConfigDialog from '../components/citas/AgendaConfigDialog';
import PdfPreviewDialog from '../components/PdfPreviewDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Phase 27 — Sistema de citas: vista de agenda (día / semana laboral / mes).
 *
 * La vista «Mes» (dirección, 14/10/2026) enseña, para las agendas elegidas,
 * qué días tienen consulta de mañana y de tarde, cuáles están cerrados y
 * cuántos pacientes hay en cada módulo (components/citas/AgendaMes.jsx, con
 * GET /api/citas/mes). Las flechas pasan de mes en mes y pinchar un día abre
 * la vista «Día» de esa fecha con las mismas agendas. Como «Día» y «Semana»,
 * el modo no se guarda: la Agenda abre siempre en «Día».
 *
 * - Selector de agenda (pills; oculto si el usuario solo ve una)
 * - Navegación de fecha (← Hoy →, salto por calendario)
 * - Click en hueco laborable → nueva cita; click en cita → detalle
 * - Admin: bloqueos de agenda y configuración (horario, huecos, overrides)
 */

/** Lunes de la semana de una fecha "YYYY-MM-DD". */
function mondayOf(dateStr) {
  const d = strToDate(dateStr);
  return addDaysStr(dateStr, -((d.getDay() + 6) % 7));
}

function dayLabel(dateStr) {
  const s = strToDate(dateStr).toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Varias agendas a la vez (administración, 5/10/2026): por debajo de este
// ancho por agenda la columna es «estrecha» y deja la regla de horas (56 px que
// con muchas agendas eran media columna) para que quepa el nombre. La hora
// sigue en la primera línea de cada cita.
const ANCHO_AGENDA_CON_REGLA = 240;
// Separación entre columnas: la de siempre, y más justa cuando son estrechas.
const HUECO_COLUMNAS = 12; // gap-3
// El móvil (revisión del 5/10/2026): ahí varias agendas se desplazan de lado,
// cada una con este ancho mínimo, como antes.
const CONSULTA_MOVIL = '(max-width: 767px)';
const ANCHO_COLUMNA_MOVIL = 280;
const HUECO_COLUMNAS_ESTRECHAS = 6; // gap-1.5

function weekLabel(monday) {
  const from = strToDate(monday);
  const to = strToDate(addDaysStr(monday, 4));
  const fmt = (d, withMonth) =>
    withMonth ? d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : String(d.getDate());
  const sameMonth = from.getMonth() === to.getMonth();
  return `${fmt(from, !sameMonth)} – ${fmt(to, true)}`;
}

// «lunes 6 de octubre» (el día dentro de una frase): `diaEnFrase`, de
// lib/citasApi.js, la misma que usa la vista «Mes» (14/10/2026).

/**
 * Qué cuenta el número que va junto al nombre de cada agenda (administración,
 * 5/10/2026), dicho en el título para quien pase el ratón por encima. La regla
 * la aplica el servidor (`pacientes_por_agenda`, citas_router.py), y es la del
 * contador de pacientes del día: personas y no citas, solo con cita activa
 * (ni anuladas ni «no acude», dirección, 8/9/2026, punto 12).
 */
function tituloPacientes(agenda, n, fecha) {
  const cuantos = n === 1 ? '1 paciente citado' : `${n} pacientes citados`;
  const enfermeria = agenda.es_enfermeria
    ? ' En enfermería, también los de su perfil citados en otras consultas.'
    : '';
  return `${agenda.name}: ${cuantos} el ${diaEnFrase(fecha)}. `
    + 'Cada paciente cuenta una vez aunque tenga dos citas ese día; '
    + `no cuentan las anuladas ni las «no acude».${enfermeria}`;
}

// El número de pacientes junto a cada agenda (`NumeroPacientes`) y las
// píldoras de elegir agenda vivían aquí. Desde el 6/10/2026 el número está en
// components/citas/NumeroPacientes.jsx y las píldoras son el selector de
// agendas (components/citas/SelectorAgendas.jsx), que ocupa una sola línea.

// «Todas» (administración, 5/10/2026): se recuerda aparte de qué agendas hay
// elegidas, porque ya no es lo mismo «todas elegidas a mano» que «Todas».
const CLAVE_MODO_TODAS = 'cjo-agendas-todas';
function leerModoTodas() {
  try { return localStorage.getItem(CLAVE_MODO_TODAS) === '1'; } catch { return false; }
}
/**
 * La primera vez tras el cambio (sin la clave del modo), «Todas» si la
 * selección guardada tiene TODAS las agendas que llegan: antes del 5/10/2026
 * eso era pulsar «Todas». Escribe la clave ('1' o '0') para no repetirlo.
 * Devuelve si queda en «Todas».
 */
function migrarModoTodas(agendas) {
  try {
    const clave = localStorage.getItem(CLAVE_MODO_TODAS);
    if (clave !== null) return clave === '1';
    const guardadas = JSON.parse(localStorage.getItem('cjo-agendas-seleccionadas') || '[]');
    const todas = Array.isArray(guardadas) && agendas.length > 1
      && agendas.every((a) => guardadas.includes(a.id));
    localStorage.setItem(CLAVE_MODO_TODAS, todas ? '1' : '0');
    return todas;
  } catch {
    return false;
  }
}

export default function CitasPage() {
  const { user } = useAuth();
  const clinicaDatos = useClinica();
  const admin = isAdminRole(user);
  // Gestión de agenda (admin/dirección/administrativo/enfermería): pueden clicar
  // fuera de horario / sobre bloqueos para ajustar el hueco (el backend valida).
  const puedeForzarHueco = canForceSlot(user);
  // Interfaz simplificada para doctores: agenda limpia, sin gestión
  // administrativa (recordatorios, lista de espera).
  const doctor = isDoctor(user);
  // El cuadro del Agente IA, solo a quien tiene el Agente (D7, 22/9/2026:
  // administración, dirección y enfermería). La agenda es la pantalla de
  // entrada del doctor y le ofrecía «Pídele aquí la cita…», que el servidor
  // le contesta con un 403.
  const agente = tieneAgente(user);

  const [agendas, setAgendas] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [loadingBase, setLoadingBase] = useState(true);
  // Agendas visibles (1 o varias). Se recuerdan entre pantallas y sesiones:
  // al salir a Recetas y volver, la agenda elegida sigue siendo la misma.
  const [selectedIds, setSelectedIdsRaw] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem('cjo-agendas-seleccionadas') || '[]');
      return Array.isArray(v) ? v.filter(Boolean) : [];
    } catch { return []; }
  });
  // Agendas abiertas desde la dirección (?fecha=…&agendas=…, Disponibilidad
  // Dres., 8/10/2026): se ENSEÑAN sin guardarse, para no pisar la selección
  // que cada uno tiene recordada. Mientras esto está a true, la selección y
  // el «Todas» guardados siguen siendo los de antes; en cuanto la persona
  // cambia a mano lo que ve, se guarda lo que elige, como siempre (y con ello
  // el «Todas» apagado que está viendo).
  const seleccionDeLaUrl = useRef(false);
  const setSelectedIds = useCallback((valor) => {
    if (seleccionDeLaUrl.current) {
      seleccionDeLaUrl.current = false;
      try { localStorage.setItem(CLAVE_MODO_TODAS, '0'); } catch {}
    }
    setSelectedIdsRaw((prev) => {
      const next = typeof valor === 'function' ? valor(prev) : valor;
      try { localStorage.setItem('cjo-agendas-seleccionadas', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);
  // «Todas» pulsado (administración, 5/10/2026): solo se ven las agendas con
  // pacientes citados ese día. Con «Todas» la selección son todas las
  // agendas, como antes; lo que cambia es qué columnas se pintan. Va aparte de
  // la selección porque elegir a mano una a una hasta tenerlas todas NO es
  // «Todas»: ahí se ven todas las elegidas, también las vacías (si no, la
  // última que se eligiera desaparecería en el mismo clic). Se recuerda como
  // la selección.
  const [modoTodas, setModoTodasRaw] = useState(leerModoTodas);
  const setModoTodas = useCallback((valor) => {
    seleccionDeLaUrl.current = false;
    setModoTodasRaw(!!valor);
    try { localStorage.setItem(CLAVE_MODO_TODAS, valor ? '1' : '0'); } catch {}
  }, []);
  const [iaOpen, setIaOpen] = useState(false);
  // Cuadro de IA a la vista (2026-07-25): lo escrito aquí abre el chat del
  // Agente IA ya lanzado con la petición. Desde 2026-07-26 es EL MISMO agente
  // del menú y del buscador — antes la agenda tenía su propia IA aparte y
  // había tres sitios distintos donde escribir lo mismo. Propone, tú confirmas.
  const [iaCuadro, setIaCuadro] = useState('');
  const [iaQuery, setIaQuery] = useState('');

  const lanzarIaCuadro = () => {
    const t = iaCuadro.trim();
    if (!t) return;
    setIaQuery(t);
    setIaCuadro('');
    setIaOpen(true);
  };
  const [recordatoriosOpen, setRecordatoriosOpen] = useState(false);
  const [date, setDate] = useState(() => todayStr());
  const [view, setView] = useState('dia'); // 'dia' | 'semana' | 'mes'
  // La vista «Mes» (14/10/2026): lo que contestó el servidor y de qué mes y
  // qué agendas es (`clave`), para no pintar nunca el mes o las agendas de
  // antes mientras llegan los nuevos. Solo cuenta la última petición.
  const [mesDatos, setMesDatos] = useState({ clave: null, datos: null, error: false });
  const turnoMes = useRef(0);

  const [citas, setCitas] = useState([]);
  // Con qué citas se decide el ORDEN de las columnas con varias agendas a la
  // vez (administración, 5/10/2026: las que tienen citas a la izquierda, las
  // vacías a la derecha). Va aparte de `citas` a propósito: se rehace al
  // cambiar de día o de agendas y al crear, mover o anular una cita desde
  // esta pantalla, pero NO con el auto-refresco de cada 30 s. Si un
  // compañero da la primera cita en una agenda vacía, la cita aparece en su
  // columna, pero las columnas no se mueven bajo el ratón de quien está
  // trabajando: se ordenan en el siguiente gesto de verdad.
  const [ordenDelDia, setOrdenDelDia] = useState({ fecha: null, citas: [] });
  // Pacientes de cada agenda en el día que se mira, {agenda_id: número}, para
  // ponerlo junto a su nombre (administración, 5/10/2026). Lo cuenta el
  // servidor (`recuentoDelDia`) y se pide con cada recarga, también la del
  // auto-refresco: un número que cambia no mueve ninguna columna, así que
  // puede ir siempre al día. `fecha` = de qué día es lo que hay guardado.
  const [recuento, setRecuento] = useState({ fecha: null, pacientes: null });
  // El día que se está mirando AHORA, para tirar el recuento que llegue tarde
  // de un día que ya se ha dejado (al pasar días deprisa).
  const fechaVista = useRef(date);
  useEffect(() => { fechaVista.current = date; }, [date]);
  // Médicos visitantes de cada columna con varias agendas a la vez
  // (administración, 7/10/2026), {agenda_id: [{nombre, especialidad}]}, del
  // día `fecha`. Una sola petición para todas las agendas elegidas; solo
  // cuenta la última (al cambiar deprisa de día o de agendas).
  const [visitantes, setVisitantes] = useState({ fecha: null, porAgenda: null });
  const turnoVisitantes = useRef(0);
  // «Todas» (Agendas disponibles, administración, 14/10/2026): cómo está cada
  // agenda el día `fecha` —disponible, bloqueada, de vacaciones o con el día
  // libre—, {agenda_id: {estado, motivo}}; null si no se sabe. Una sola
  // petición para todas (GET /api/agendas-disponibles/dia). Va con el orden
  // «congelado» de las columnas: se pide con los gestos de quien mira
  // (cambiar de día, crear, mover o anular aquí), no con el auto-refresco.
  const [estadoDia, setEstadoDia] = useState({ fecha: null, porAgenda: null });
  const fechaEstadoDia = useRef(null);
  const [bloqueos, setBloqueos] = useState([]);
  // Vista "Todas": bloqueos por agenda (cada AgendaGrid recibe SOLO los suyos,
  // porque el grid no filtra bloqueos por agenda).
  const [bloqueosPorAgenda, setBloqueosPorAgenda] = useState({});
  const [loadingData, setLoadingData] = useState(false);
  // La agenda solo se atenúa si la carga tarda de verdad (>250 ms): con la
  // respuesta local típica el fundido no llegaba a completarse y cada ‹ ›
  // era un parpadeo de la rejilla entera (auditoría de animación 2026-08-21).
  const [cargaLenta, setCargaLenta] = useState(false);
  useEffect(() => {
    if (!loadingData) { setCargaLenta(false); return undefined; }
    const t = setTimeout(() => setCargaLenta(true), 250);
    return () => clearTimeout(t);
  }, [loadingData]);

  // Diálogos
  const [createSlot, setCreateSlot] = useState(null); // {date, start} | null
  const [detailCita, setDetailCita] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [bloqueoOpen, setBloqueoOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null); // {url, filename, title} | null
  // Elegir agenda y día cuando hay varias seleccionadas o se está en semana.
  const [imprimirPicker, setImprimirPicker] = useState(null); // {perfil, agendaId, dia} | null
  const [icalOpen, setIcalOpen] = useState(false);
  const [icalUrl, setIcalUrl] = useState('');
  const [icalCopied, setIcalCopied] = useState(false);

  // Multi-selección de agendas: se pueden ver VARIAS a la vez (2026-07-17).
  // - 1 agenda seleccionada → vista día/semana normal (`agenda`).
  // - 2+ seleccionadas → vista "varias a la vez" (solo día): N grids.
  const multiView = selectedIds.length > 1;
  const allView = multiView; // alias interno (varios grids a la vez)
  // «Todas» de verdad: pulsada y con más de una agenda elegida.
  const vistaTodas = modoTodas && multiView;

  const agenda = useMemo(
    () => (selectedIds.length === 1 ? agendas.find((a) => a.id === selectedIds[0]) || null : null),
    [agendas, selectedIds],
  );
  const selectedAgendas = useMemo(
    () => agendas.filter((a) => selectedIds.includes(a.id)),
    [agendas, selectedIds],
  );

  // Columnas de «varias a la vez», ordenadas por contenido (administración,
  // 5/10/2026): primero las que tienen alguna cita ese día, luego las vacías;
  // dentro de cada grupo, el orden de siempre. Qué cuenta como «tener citas»
  // y por qué, en `ordenarAgendasPorContenido` (lib/citasApi.js). Las
  // píldoras de arriba no se reordenan: se mueven las columnas.
  //
  // Con «Todas» (administración, 5/10/2026) las vacías ni salen: «únicamente
  // deben mostrarse las agendas que tienen pacientes citados ese día», para ver
  // de un vistazo la actividad del día sin pasar por columnas vacías. «Tener
  // pacientes» es lo mismo que para el orden (`agendaTieneCitas`) y sale del
  // mismo orden «congelado», así que aparecer y desaparecer van como moverse:
  //  - la primera cita en una agenda vacía, dada desde esta pantalla (o por
  //    el Agente IA), la hace aparecer en el acto: es el gesto de quien mira;
  //  - anular aquí la última cita de una agenda la quita en el acto, por lo
  //    mismo;
  //  - lo que cambia un compañero llega con el auto-refresco: la cita se ve (o
  //    deja de verse) en su columna y el número de la píldora se pone al día,
  //    pero ninguna columna aparece ni desaparece bajo el ratón. Lo hace en el
  //    siguiente gesto de verdad (cambiar de día; crear, mover o anular una
  //    cita aquí) o con el aviso «Poner al día» que sale encima de la agenda
  //    en cuanto hay algo que poner, que es un clic y no una espera.
  // Con una selección hecha a mano se ven todas las elegidas, vacías o no.
  //
  // Desde el 14/10/2026 (Agendas disponibles), «Todas» enseña además, detrás
  // de las que tienen pacientes, las de los doctores DISPONIBLES ese día y, al
  // final, las bloqueadas y las de doctores de vacaciones o con el día libre
  // (`columnasDeTodas`). Sin saber el estado del día, como antes.
  const estadoDelOrden = estadoDia.fecha && estadoDia.fecha === ordenDelDia.fecha ? estadoDia.porAgenda : null;
  const columnasMulti = useMemo(() => (vistaTodas
    ? columnasDeTodas(selectedAgendas, ordenDelDia.citas, ordenDelDia.fecha, estadoDelOrden)
    : ordenarAgendasPorContenido(selectedAgendas, ordenDelDia.citas, ordenDelDia.fecha)
  ), [selectedAgendas, ordenDelDia, vistaTodas, estadoDelOrden]);
  const idsColumnas = useMemo(() => new Set(columnasMulti.map((a) => a.id)), [columnasMulti]);

  // La vista «Mes» (dirección, 14/10/2026): el mes de la fecha que se mira y
  // las agendas elegidas. Lo que llegó del servidor solo vale si es de ESTE
  // mes y ESTAS agendas.
  const mesVisto = primeroDelMes(date);
  const claveMes = `${mesVisto}|${selectedIds.join(',')}`;
  const datosMes = view === 'mes' && mesDatos.clave === claveMes ? mesDatos.datos : null;
  const errorMes = view === 'mes' && mesDatos.clave === claveMes && !!mesDatos.error;
  // Qué agendas salen en el mes. Elegidas a mano, todas las elegidas, como en
  // «Día». Con «Todas», las que tienen algo ESE MES: abiertas algún día
  // (aunque sea en parte) o con algún paciente citado. Es el «solo las que
  // tienen pacientes» del día llevado al mes: con «solo con pacientes», las
  // primeras semanas de un mes que aún no se ha citado saldrían vacías y no
  // se vería qué consultas hay para dar cita. Una agenda sin horario ni
  // citas en todo el mes (o bloqueada el mes entero) no sale.
  const agendasMes = useMemo(() => {
    if (!vistaTodas) return selectedAgendas;
    if (!datosMes) return [];
    return selectedAgendas.filter((a) => conActividadEnElMes(a.id, datosMes));
  }, [vistaTodas, selectedAgendas, datosMes]);
  const idsMes = useMemo(() => new Set(agendasMes.map((a) => a.id)), [agendasMes]);
  // Lo que el auto-refresco ha traído y las columnas de «Todas» aún no
  // enseñan: agendas que ya tienen pacientes (`entran`) y columnas que se han
  // quedado vacías (`salen`). Solo con el orden del día que se mira (mientras
  // carga otro día no hay nada que comparar).
  const cambiosEnTodas = useMemo(() => {
    if (!vistaTodas || ordenDelDia.fecha !== date) return null;
    const entran = selectedAgendas.filter((a) => !idsColumnas.has(a.id) && agendaTieneCitas(a, citas, date));
    // Una disponible, bloqueada o de vacaciones se sigue viendo sin citas.
    const salen = columnasMulti.filter((a) => !agendaTieneCitas(a, citas, date)
      && !seVeEnTodasSinCitas(a, estadoDelOrden));
    return entran.length || salen.length ? { entran, salen } : null;
  }, [vistaTodas, ordenDelDia.fecha, date, selectedAgendas, idsColumnas, columnasMulti, citas, estadoDelOrden]);

  // Ancho disponible para las columnas de «varias a la vez» (5/10/2026). Con
  // él se decide si caben con su regla de horas o si van estrechas. Se mide
  // antes de pintar (layout effect): si no, al abrir con diez agendas se veía
  // un instante la regla de horas y luego desaparecía.
  const [multiEl, setMultiEl] = useState(null);
  const [anchoMulti, setAnchoMulti] = useState(0);
  useLayoutEffect(() => {
    if (!multiEl) return undefined;
    setAnchoMulti(multiEl.clientWidth || 0);
    if (typeof window.ResizeObserver === 'undefined') return undefined;
    const ro = new window.ResizeObserver((entries) => {
      const w = entries?.[0]?.contentRect?.width;
      setAnchoMulti(Math.round(w ?? multiEl.clientWidth ?? 0));
    });
    ro.observe(multiEl);
    return () => ro.disconnect();
  }, [multiEl]);
  const anchoPorAgenda = anchoMulti > 0 && columnasMulti.length > 0
    ? (anchoMulti - (columnasMulti.length - 1) * HUECO_COLUMNAS) / columnasMulti.length
    : null;
  // En el MÓVIL, varias agendas siguen como antes: cada columna con su ancho
  // y desplazándose de lado (revisión del 5/10/2026). Repartir 375 px entre
  // cuatro agendas las dejaba ilegibles; lo de «todas a la vista» es para la
  // pantalla de recepción.
  const [esMovilMulti, setEsMovilMulti] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.(CONSULTA_MOVIL)?.matches);
  useEffect(() => {
    const mq = typeof window !== 'undefined' ? window.matchMedia?.(CONSULTA_MOVIL) : null;
    if (!mq?.addEventListener) return undefined;
    const alCambiar = (e) => setEsMovilMulti(!!e.matches);
    mq.addEventListener('change', alCambiar);
    return () => mq.removeEventListener('change', alCambiar);
  }, []);
  const multiEstrecha = !esMovilMulti && anchoPorAgenda != null
    && anchoPorAgenda < ANCHO_AGENDA_CON_REGLA;

  // Alternar una agenda en la selección (sin dejar 0 seleccionadas).
  // Con «Todas» (5/10/2026), las píldoras encendidas son las columnas que se
  // ven y una píldora se alterna sobre ESO: la de una agenda que no sale
  // (vacía) la añade a las que se ven —así se da cita en una agenda vacía:
  // eligiendo su píldora— y la de una que sale la quita. Las dos dejan
  // «Todas» y pasan a una selección a mano, que enseña todas las elegidas.
  // Sin ninguna columna a la vista, elegir una píldora abre esa agenda sola.
  // (6/10/2026: las píldoras son ahora las casillas del selector de agendas y
  // las «x» de sus chips; la regla es la misma.)
  // En la vista «Mes» (14/10/2026), lo que se ve son las agendas del mes.
  const togglePill = (id) => {
    if (vistaTodas) {
      const vistas = (view === 'mes' ? agendasMes : columnasMulti).map((a) => a.id);
      setModoTodas(false);
      if (!vistas.includes(id)) setSelectedIds([...vistas, id]);
      else setSelectedIds(vistas.length === 1 ? vistas : vistas.filter((x) => x !== id));
      return;
    }
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.length === 1 ? prev : prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };
  // «Todas»: un interruptor, como siempre. Encenderlo elige todas las agendas
  // y solo enseña las que tienen pacientes; apagarlo vuelve a la primera.
  const pulsarTodas = () => {
    if (vistaTodas) {
      setModoTodas(false);
      setSelectedIds([agendas[0].id]);
    } else {
      setModoTodas(true);
      setSelectedIds(agendas.map((a) => a.id));
      // El orden guardado puede ser de otra vista (una sola agenda, o varias
      // de antes) y, comparado con las citas de ahora, daba un aviso falso
      // de «X se ha quedado sin citas» (revisión del 5/10/2026). Sin orden,
      // sale el hueco de la agenda hasta que llega el día.
      setOrdenDelDia({ fecha: null, citas: [] });
    }
  };
  // «Solo esta» del selector de agendas (dirección, 6/10/2026): esa agenda
  // sola, se viniera de «Todas» o de varias elegidas a mano. Es lo mismo que
  // hace el acceso directo a una cita (?cita=…) con su agenda.
  const verSolo = (id) => {
    setModoTodas(false);
    setSelectedIds([id]);
  };
  // Con varias agendas solo tiene sentido la vista de día.
  useEffect(() => {
    if (selectedIds.length > 1 && view === 'semana') setView('dia');
  }, [selectedIds, view]);

  const monday = useMemo(() => mondayOf(date), [date]);
  const weekDates = useMemo(() => [0, 1, 2, 3, 4].map((i) => addDaysStr(monday, i)), [monday]);

  // Carga inicial: agendas (ya filtradas por rol y ordenadas por profesional
  // en el servidor, 5/10/2026) + conceptos.
  // Los conceptos van CON los retirados (revisión 2026-09-03): aquí solo
  // sirven para el color y el nombre de cada cita en la rejilla y el resumen,
  // y las cirugías ya dadas con «Cirugía»/«Cirugía menor» (retirados en el
  // punto 4) tienen que seguir viéndose en azul. Los desplegables de alta
  // cargan los suyos (solo activos).
  useEffect(() => {
    let cancelled = false;
    setLoadingBase(true);
    Promise.all([listAgendas(), listConceptos({ todos: true })])
      .then(([ags, cons]) => {
        if (cancelled) return;
        const list = Array.isArray(ags) ? ags : [];
        setAgendas(list);
        setConceptos(Array.isArray(cons) ? cons : []);
        // Quien ya usaba «Todas» antes del 5/10/2026 tiene guardadas todas
        // las agendas y no tiene la clave del modo: tras el despliegue seguiría
        // viendo las vacías y parecería que no funciona. Se decide UNA vez:
        // con todas guardadas, «Todas»; con menos, selección a mano. Se escribe
        // la clave en los dos casos para no volver a decidirlo.
        if (list.length > 1) setModoTodasRaw(migrarModoTodas(list));
        if (list.length > 0) {
          setSelectedIds((prev) => {
            // Con «Todas» recordada, todas las de hoy: también una agenda
            // dada de alta desde la última vez (5/10/2026).
            if (leerModoTodas() && list.length > 1) return list.map((a) => a.id);
            // La selección recordada puede traer agendas que ya no existen o
            // que este usuario no puede ver: se filtra contra la lista real.
            const validas = prev.filter((id) => list.some((a) => a.id === id));
            return validas.length ? validas : [list[0].id];
          });
        }
      })
      .catch((e) => {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'No se pudieron cargar las agendas');
      })
      .finally(() => {
        if (!cancelled) setLoadingBase(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Contador para refrescar componentes dependientes (barra de quirófano).
  const [reloadKey, setReloadKey] = useState(0);

  // Huecos de cancelaciones con candidatos en espera → badge del botón Espera.
  // Se refresca con cada reload (p. ej. tras cancelar una cita) y al cerrar el panel.

  // Carga de citas + bloqueos del rango visible. Con `silent` refresca en
  // segundo plano: sin atenuar la rejilla ni bloquear clics (lo usa el
  // auto-refresco de los pilotos de estado).
  // Las citas «no acude» se piden también (dirección, 2026-09-08, punto 12):
  // no desaparecen de la agenda, se quedan en su hueco con el nombre tachado.
  // `reordenar` (5/10/2026): un refresco en silencio que SÍ rehace el orden de
  // las columnas, el que sigue a crear, mover o anular una cita aquí.
  const reloadDia = useCallback(async ({ silent = false, reordenar = false } = {}) => {
    if (selectedIds.length === 0) return;
    if (!silent) {
      setReloadKey((k) => k + 1);
      setLoadingData(true);
    }
    // El número de pacientes de cada agenda (5/10/2026), con TODA recarga
    // del día —cambio de día, crear, mover, anular y el auto-refresco— y
    // aparte de las citas: si falla, la agenda se pinta igual, sin números.
    // En la vista semana no se pide: ahí las píldoras no llevan número. Ni
    // con una sola agenda en la lista (un doctor con la suya): no hay
    // píldoras donde ponerlo.
    if (agendas.length > 1 && (multiView || view === 'dia')) {
      const pedida = date;
      const guardar = (pacientes) => {
        // Si ya se mira otro día, esta respuesta llega tarde: fuera.
        if (fechaVista.current !== pedida) return;
        setRecuento((prev) => {
          if (pacientes) return { fecha: pedida, pacientes };
          // Sin respuesta: los del mismo día se quedan (un refresco que falla
          // no borra nada); los de otro día no se enseñan nunca.
          return prev.fecha === pedida ? prev : { fecha: pedida, pacientes: null };
        });
      };
      recuentoDelDia(pedida).then(guardar, () => guardar(null));
    }
    // Quién viene de visita a cada columna (administración, 7/10/2026): con
    // varias agendas a la vista, en UNA petición para todas las elegidas (con
    // «Todas», también las vacías que no salen: si estrenan cita y aparecen,
    // ya está). Va con toda recarga, como el número de pacientes: al cambiar
    // de día, al volver de una agenda suelta donde se acaba de apuntar (cambia
    // la selección y se recarga) y con el auto-refresco, que trae lo que
    // apunte un compañero. Aparte de las citas: si falla, la agenda se pinta
    // igual, sin la línea. (Mientras no han llegado las agendas, nada que
    // pedir: la selección guardada aún no tiene a qué agendas corresponde.)
    if (multiView && selectedAgendas.length > 0) {
      const pedida = date;
      turnoVisitantes.current += 1;
      const mio = turnoVisitantes.current;
      const guardarVisitantes = (porAgenda) => {
        if (turnoVisitantes.current !== mio || fechaVista.current !== pedida) return;
        setVisitantes((prev) => {
          if (porAgenda) return { fecha: pedida, porAgenda };
          // Un refresco que falla no borra lo del mismo día.
          return prev.fecha === pedida ? prev : { fecha: pedida, porAgenda: null };
        });
      };
      medicosVisitantesDelDia(pedida, selectedAgendas.map((a) => a.id))
        .then(guardarVisitantes, () => guardarVisitantes(null));
    }
    try {
      if (multiView) {
        // Varias agendas, vista día: 1 petición de citas (sin agenda_id → el
        // staff ve todas; cada grid filtra las suyas) + bloqueos por agenda.
        // Con «Todas», además el estado de cada agenda ese día (14/10/2026),
        // con los gestos de quien mira y no con el refresco callado, como el
        // orden de las columnas; si falla, «no se sabe» (solo pacientes).
        // (Mientras no han llegado las agendas, nada que pedir.)
        const pedirEstado = vistaTodas && selectedAgendas.length > 0
          && (!silent || reordenar || fechaEstadoDia.current !== date);
        const [cs, bsArr, estadoNuevo] = await Promise.all([
          listCitas({ date, includeNoAcude: true }),
          Promise.all(
            selectedAgendas.map((a) =>
              listBloqueos(a.id, { dateFrom: date, dateTo: date }).catch(() => []),
            ),
          ),
          pedirEstado ? estadoDeAgendasDelDia(date).catch(() => null) : Promise.resolve(undefined),
        ]);
        if (estadoNuevo !== undefined && fechaVista.current === date) {
          fechaEstadoDia.current = date;
          setEstadoDia({ fecha: date, porAgenda: estadoNuevo });
        }
        const lista = Array.isArray(cs) ? cs : [];
        setCitas(lista);
        // El refresco callado no reordena (que no salten las columnas bajo el
        // ratón), salvo que el orden sea de otro día o no lo haya: una
        // primera carga fallida o dos respuestas cruzadas al pasar de día
        // deprisa (revisión del 5/10/2026).
        setOrdenDelDia((prev) => (!silent || reordenar || prev?.fecha !== date
          ? { fecha: date, citas: lista } : prev));
        setBloqueosPorAgenda(Object.fromEntries(selectedAgendas.map((a, i) => [a.id, bsArr[i] || []])));
      } else {
        const soloId = selectedIds[0];
        const range =
          view === 'semana'
            ? { dateFrom: monday, dateTo: addDaysStr(monday, 4) }
            : { dateFrom: date, dateTo: date };
        const [cs, bs] = await Promise.all([
          view === 'semana'
            ? listCitas({ agendaId: soloId, dateFrom: range.dateFrom, dateTo: range.dateTo, includeNoAcude: true })
            : listCitas({ agendaId: soloId, date, includeNoAcude: true }),
          listBloqueos(soloId, range),
        ]);
        setCitas(Array.isArray(cs) ? cs : []);
        setBloqueos(Array.isArray(bs) ? bs : []);
      }
    } catch (e) {
      // En silencio no molestamos: si un refresco de fondo falla (wifi, etc.)
      // la agenda se queda como estaba y el siguiente intento lo corrige.
      if (!silent) toast.error(e?.response?.data?.detail || 'No se pudo cargar la agenda');
    } finally {
      if (!silent) setLoadingData(false);
    }
  }, [selectedIds, date, view, monday, multiView, selectedAgendas, agendas.length, vistaTodas]);

  // La vista «Mes» (14/10/2026): UNA petición por mes con todas las agendas
  // elegidas (con «Todas», todas: el servidor dice cuáles tienen algo). Nada
  // del día: ni citas, ni número de cada agenda, ni visitantes. Depende del
  // MES y de las agendas, no del día: cambiar de día dentro del mismo mes (con
  // el calendario de la fecha) no la vuelve a pedir ni atenúa la rejilla.
  // Con el auto-refresco se pone al día en silencio. Si falla y no hay nada
  // de este mes, la pantalla avisa con «Reintentar» (antes se quedaba
  // «cargando» hasta el siguiente refresco); un refresco en silencio que
  // falla deja lo que había.
  const cargarMes = useCallback(async ({ silent = false } = {}) => {
    if (selectedIds.length === 0) return;
    const clave = `${mesVisto}|${selectedIds.join(',')}`;
    turnoMes.current += 1;
    const mio = turnoMes.current;
    if (!silent) setLoadingData(true);
    try {
      const datos = await resumenDelMes(mesVisto, selectedIds);
      if (!datos) throw new Error('Respuesta del mes sin días');
      if (turnoMes.current === mio) setMesDatos({ clave, datos, error: false });
    } catch {
      if (turnoMes.current === mio) {
        setMesDatos((prev) => (prev.clave === clave && prev.datos
          ? prev : { clave, datos: null, error: true }));
      }
    } finally {
      if (!silent) setLoadingData(false);
    }
  }, [mesVisto, selectedIds]);

  // La recarga de lo que se ve: el mes en la vista «Mes»; si no, el día o la
  // semana. Es la que usan el auto-refresco y los diálogos (crear, mover…).
  const reload = view === 'mes' ? cargarMes : reloadDia;

  useEffect(() => {
    reload();
  }, [reload]);

  // Los sonidos del día viven ahora en AppShell/SonidosVigilante (2026-08-20):
  // suenan en TODA la sesión, no solo con la agenda abierta.
  // Auto-refresco de la agenda: los pilotos (sala de espera, visto por
  // enfermería, luz, LOPD…) los cambian otros compañeros desde sus puestos,
  // así que la vista se pone al día sola cada 30 s — sin parpadeos ni perder
  // lo que tengas abierto — y también al volver a la pestaña.
  useEffect(() => {
    const refrescar = () => {
      if (!document.hidden) reload({ silent: true });
    };
    const id = setInterval(refrescar, 30_000);
    document.addEventListener('visibilitychange', refrescar);
    window.addEventListener('focus', refrescar);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', refrescar);
      window.removeEventListener('focus', refrescar);
    };
  }, [reload]);

  const step = view === 'semana' ? 7 : 1;
  // ‹ y ›: un día, una semana o, en la vista «Mes», un mes (el mismo día del
  // mes si existe; si no, el último: del 31 de enero, al 28 de febrero).
  const moverFecha = (sentido) => setDate(
    view === 'mes' ? sumarMeses(date, sentido) : addDaysStr(date, sentido * step));
  // Pinchar un día del mes: la vista «Día» de esa fecha, con las mismas
  // agendas (también «Todas», que entonces enseña las de ese día).
  const abrirDiaDelMes = (fecha) => {
    setDate(fecha);
    setView('dia');
  };


  // Enlace iCal del doctor (sus agendas) para suscribirse desde Google/Apple
  // Calendar. El backend crea el token la primera vez.
  const abrirIcal = async () => {
    setIcalOpen(true);
    setIcalCopied(false);
    if (icalUrl) return;
    try {
      const res = await api.get('/api/citas/ical-link');
      setIcalUrl(res.data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo generar el enlace');
      setIcalOpen(false);
    }
  };

  const copiarIcal = async () => {
    try {
      await navigator.clipboard.writeText(icalUrl);
      setIcalCopied(true);
      toast.success('Enlace copiado');
    } catch {
      toast.error('No se pudo copiar — selecciona el texto y cópialo a mano');
    }
  };

  const [icalBusy, setIcalBusy] = useState(false);
  // Añadir = abre Google Calendar con la suscripción. Reactiva por si estaba quitada.
  const anadirIcal = async () => {
    try { await api.post('/api/citas/ical-active', null, { params: { active: true } }); } catch { /* no bloqueante */ }
    window.open(`https://calendar.google.com/calendar/render?cid=${encodeURIComponent(icalUrl)}`, '_blank', 'noopener');
  };
  // Quitar = pausa el feed; Google vacía la suscripción en su próxima sync.
  const quitarIcal = async () => {
    setIcalBusy(true);
    try {
      await api.post('/api/citas/ical-active', null, { params: { active: false } });
      toast.success('Quitado de Google Calendar. Puede tardar unas horas en desaparecer allí.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo quitar');
    } finally {
      setIcalBusy(false);
    }
  };

  // Imprimir el parte del día. Con UNA agenda y en vista de día sale directo;
  // con varias agendas, o en vista de semana, hay que preguntar cuál y qué día
  // (antes el botón simplemente no aparecía en esos casos).
  const pdfDelDia = useCallback((ag, dia, perfil) => {
    const sufijo = perfil ? `&perfil=${perfil}` : '';
    const nombre = (ag.name || 'dia').replace(/ /g, '_');
    setPdfPreview({
      url: `${BACKEND_URL}/api/citas/agenda-dia/pdf?agenda_id=${ag.id}&date=${dia}${sufijo}`,
      filename: `${perfil ? 'agenda_enfermeria' : 'agenda'}_${nombre}_${dia}.pdf`,
      title: `${perfil ? clinicaDatos.agenda_perfil_enfermeria : 'Agenda del día'} · ${ag.name} · ${dia}`,
    });
  }, [clinicaDatos.agenda_perfil_enfermeria]);

  // Con «Todas» (revisión del 5/10/2026) se ofrecen las columnas que se VEN,
  // en su orden, y se propone la primera: antes salía la primera agenda de la
  // lista, aunque estuviera vacía y oculta. Sin ninguna a la vista, todas.
  const agendasParaImprimir = vistaTodas && columnasMulti.length ? columnasMulti : selectedAgendas;
  const abrirImpresion = useCallback((perfil) => {
    if (selectedAgendas.length === 1 && view === 'dia') {
      pdfDelDia(selectedAgendas[0], date, perfil);
      return;
    }
    setImprimirPicker({ perfil, agendaId: agendasParaImprimir[0]?.id || '', dia: date });
  }, [selectedAgendas, agendasParaImprimir, view, date, pdfDelDia]);

  const onSlotClick = ({ date: d, start }) => setCreateSlot({ date: d, start });
  const onCitaClick = (cita) => {
    setDetailCita(cita);
    setDetailOpen(true);
  };

  // Abrir una cita concreta desde fuera de la agenda:
  //     /citas?cita=<id>&fecha=<YYYY-MM-DD>&agenda=<id>
  // Lo usa la relación de citas de la ficha del paciente (administración,
  // 2026-09-09): lleva al día de esa cita, con su agenda seleccionada y el
  // resumen ya desplegado — que es como se ve el detalle de una cita aquí.
  // Detalles que importan:
  //  - Solo la primera vez, y la dirección se limpia: recargar o volver atrás
  //    no vuelve a abrir el resumen (mismo criterio que /informes?edit=<id>).
  //  - La cita se pide APARTE, en una petición propia, en vez de esperar a la
  //    rejilla: al cambiar de día y de agenda hay dos cargas en vuelo y no se
  //    sabe cuál contesta antes; así el resumen sale con la cita correcta.
  //  - Si la agenda no es de las que ve este usuario, o la cita ya no está
  //    (anulada, borrada), se avisa y la agenda se queda como estaba: nunca
  //    una pantalla en blanco ni un error suelto.
  const [searchParams, setSearchParams] = useSearchParams();
  const citaParamHecho = useRef(false);
  useEffect(() => {
    if (citaParamHecho.current || loadingBase) return;
    const citaId = searchParams.get('cita');
    if (!citaId) return;
    citaParamHecho.current = true;
    const fecha = searchParams.get('fecha') || todayStr();
    const agendaId = searchParams.get('agenda') || '';
    setSearchParams({}, { replace: true });
    if (agendaId && !agendas.some((a) => a.id === agendaId)) {
      toast.error('Esa cita está en una agenda que no puedes ver');
      return;
    }
    setView('dia');
    setDate(fecha);
    if (agendaId) {
      // Su agenda, sola: aunque estuviera «Todas» (5/10/2026).
      setModoTodas(false);
      setSelectedIds([agendaId]);
    }
    listCitas({ agendaId: agendaId || undefined, date: fecha, includeNoAcude: true })
      .then((cs) => {
        const encontrada = (Array.isArray(cs) ? cs : []).find((c) => c.id === citaId);
        if (!encontrada) {
          toast.error('Esa cita ya no está en la agenda: puede que se anulara');
          return;
        }
        setDetailCita(encontrada);
        setDetailOpen(true);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'No se pudo abrir la cita'));
  }, [loadingBase, agendas, searchParams, setSearchParams, setSelectedIds, setModoTodas]);

  // Abrir la agenda en un día y con unas agendas desde otra pantalla
  // (Disponibilidad Dres., administración, 8/10/2026; desde el 14/10/2026,
  // Agendas disponibles):
  //   /citas?fecha=aaaa-mm-dd&agendas=id1,id2
  // Pinchar un día de consulta abre la consulta de ese doctor ese día; uno de
  // quirófano, su quirófano; uno con las dos, las dos a la vez.
  //  - La dirección se limpia al leerla, como con ?cita= (recargar no la
  //    vuelve a aplicar).
  //  - Las agendas que este usuario no ve, o que ya no están, se quitan; si
  //    no queda ninguna, se avisa y la agenda se queda como estaba.
  //  - NO se guarda como la selección del usuario (`seleccionDeLaUrl`): al
  //    volver a la Agenda otro día, cada uno ve las agendas que tenía.
  useEffect(() => {
    if (loadingBase || searchParams.get('cita')) return;
    const agendasParam = searchParams.get('agendas');
    const fechaParam = searchParams.get('fecha');
    if (agendasParam === null && fechaParam === null) return;
    setSearchParams({}, { replace: true });
    if (esFechaValida(fechaParam)) setDate(fechaParam);
    if (agendasParam === null) return;
    const pedidas = [...new Set(agendasParam.split(',').map((x) => x.trim()).filter(Boolean))];
    const visibles = pedidas.filter((id) => agendas.some((a) => a.id === id));
    if (!visibles.length) {
      toast.error('Esa agenda no está entre las que puedes ver');
      return;
    }
    if (visibles.length < pedidas.length) toast.error('Alguna de esas agendas no está entre las que puedes ver');
    setView('dia');
    seleccionDeLaUrl.current = true;
    setModoTodasRaw(false);
    setSelectedIdsRaw(visibles);
  }, [loadingBase, agendas, searchParams, setSearchParams]);

  // El resumen de cita se veía con los datos del clic: cambiabas algo y había
  // que cerrarlo para comprobar si había entrado (2026-07-28). Ahora la cita
  // que enseña es la de la lista, y cuando se guarda algo se sustituye en el
  // sitio: se ve al momento arriba y en la rejilla, sin recargar.
  const citaViva = useMemo(
    () => citas.find((c) => c.id === detailCita?.id) || detailCita,
    [citas, detailCita],
  );
  const onCitaActualizada = useCallback((actualizada) => {
    if (actualizada?.id) {
      // Las intervenciones del paciente (14/10/2026) no vienen en todas las
      // respuestas: mientras llega la recarga de abajo se quedan las que
      // había (`conservarIntervenciones`).
      setCitas((prev) => prev.map((c) => (c.id === actualizada.id ? conservarIntervenciones(c, actualizada) : c)));
      setDetailCita((prev) => (prev?.id === actualizada.id ? conservarIntervenciones(prev, actualizada) : prev));
    }
    // Y por detrás, la recarga de siempre: al cambiar de día o de agenda la
    // cita ya no está en esta lista, y hay que traerse la agenda entera.
    // Mover o anular puede vaciar una agenda o estrenar otra: esta recarga
    // rehace también el orden de las columnas (5/10/2026).
    reload({ silent: true, reordenar: true });
  }, [reload]);

  // Empty state elegante: la agenda no abre ese día y no hay nada que pintar.
  const dayHasHours = (dstr) =>
    (agenda?.working_hours?.[String((strToDate(dstr).getDay() + 6) % 7)] || []).length > 0;
  const showDayEmpty =
    view === 'dia' && agenda && !dayHasHours(date) && citas.filter((c) => c.date === date).length === 0;
  const showWeekEmpty =
    view === 'semana' && agenda && !weekDates.some(dayHasHours) && citas.length === 0;

  let subtitle = 'Agenda de consultas de la clínica';
  if (vistaTodas && view === 'mes') subtitle = 'Todas las agendas abiertas o con citas este mes';
  else if (vistaTodas) subtitle = 'Agendas con pacientes, doctores disponibles y bloqueadas este día';
  else if (multiView) subtitle = `${selectedIds.length} agendas a la vez`;
  else if (agenda) subtitle = `${agenda.name}${agenda.doctor_name ? ` · ${agenda.doctor_name}` : ''}`;

  // Contador de pacientes del día visible. Desde el 5/10/2026 cuenta con la
  // MISMA regla que el número de cada agenda (`pacientesCitados`, la de
  // `pacientes_por_agenda` en el servidor): PERSONAS con cita activa, sin
  // las anuladas ni las «no acude» (dirección, 8/9/2026, punto 12). Antes
  // contaba citas, y el mismo paciente con dos citas salía dos veces. Con
  // varias agendas, lo de las columnas que se ven (en la de enfermería,
  // también las prestadas), y quien esté en dos columnas cuenta una vez.
  const pacientesDia = useMemo(() => {
    const enPantalla = multiView
      ? columnasMulti.flatMap((a) => citasDeLaColumna(a, citas))
      : citas;
    return pacientesCitados(enPantalla, date);
  }, [citas, date, multiView, columnasMulti]);

  // El número junto al nombre de cada agenda (5/10/2026): en la vista de día,
  // con una agenda o con varias. En la vista semana de una agenda, las
  // píldoras no lo llevan: el número es «pacientes citados ESE día» y en la
  // semana no hay un día; uno de la semana entera se leería igual y diría otra
  // cosa (otra regla, además: el mismo paciente en dos días). En la vista
  // «Mes» (14/10/2026), por lo mismo, tampoco: los números van en cada día del
  // mes. (Con varias agendas la vista es siempre «Día» o «Mes».)
  const numerosPorAgenda = view === 'dia' ? recuento.pacientes : null;
  const numerosAlDia = recuento.fecha === date;
  const numeroDe = (a) => {
    const n = numerosPorAgenda?.[a.id];
    return Number.isFinite(n) ? n : null;
  };
  // Los médicos visitantes de una columna, solo los del día que se mira.
  const visitantesDe = (a) => {
    const lista = visitantes.fecha === date ? visitantes.porAgenda?.[a.id] : null;
    return Array.isArray(lista) ? lista : [];
  };
  // Si alguna columna a la vista tiene visitante, las demás guardan el hueco
  // de esa línea (en blanco) para que todas las rejillas sigan empezando a la
  // par. Si no lo tiene ninguna, no hay línea ni hueco en ninguna.
  const hayVisitantesEnColumnas = multiView && columnasMulti.some((a) => visitantesDe(a).length > 0);
  // Con «Todas» (14/10/2026): «Disponible», «Vacaciones», «Bloqueada…» bajo
  // el nombre de cada columna, del día que se mira. Igual que con el
  // visitante, si alguna lo lleva las demás guardan el hueco.
  const estadoDe = (a) => {
    if (!vistaTodas || estadoDia.fecha !== date) return null;
    const e = estadoDia.porAgenda?.[a.id];
    return e && ESTADOS_CON_LINEA.includes(e.estado) ? e : null;
  };
  const hayEstadosEnColumnas = vistaTodas && columnasMulti.some((a) => estadoDe(a));
  // Lo que dice el aviso de «Todas» cuando un compañero ha cambiado algo.
  // Dice «citas» y no «pacientes»: una columna con solo una «no acude» se
  // sigue viendo (hay algo que mirar) aunque su número sea 0.
  const textoCambiosEnTodas = cambiosEnTodas ? [
    cambiosEnTodas.entran.length === 1
      ? `${cambiosEnTodas.entran[0].name} ya tiene citas`
      : cambiosEnTodas.entran.length > 1
        ? `${cambiosEnTodas.entran.map((a) => a.name).join(', ')} ya tienen citas`
        : '',
    cambiosEnTodas.salen.length === 1
      ? `${cambiosEnTodas.salen[0].name} se ha quedado sin citas`
      : cambiosEnTodas.salen.length > 1
        ? `${cambiosEnTodas.salen.map((a) => a.name).join(', ')} se han quedado sin citas`
        : '',
  ].filter(Boolean).join('; ') : '';
  // «Poner al día» es un gesto: rehace las columnas con lo que hay ahora.
  const ponerAlDiaTodas = () => setOrdenDelDia({ fecha: date, citas });
  // «X pacientes en N agendas»: con «Todas», N son solo las columnas CON
  // pacientes (revisión del 14/10/2026: desde que «Todas» enseña también las
  // de doctores disponibles y las bloqueadas, contaba esas). Con la misma
  // regla que X (`pacientesCitados`).
  const agendasEnContador = vistaTodas
    ? columnasMulti.filter((a) => pacientesCitados(citasDeLaColumna(a, citas), date) > 0).length
    : selectedIds.length;

  return (
    <div>
      <PageHeader title="Agenda" subtitle={subtitle} />

      {/* Fila de controles sticky */}
      <div className="sticky top-0 z-30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-2.5 bg-white/95 backdrop-blur border-b border-[#D9E3DD] mb-4">
        {/* Fila de arriba (2026-07-26): SOLO qué agenda estás mirando. Bloqueo y
            Gestión de agendas iban aquí al lado y se leían como una agenda más;
            bajan con el resto de herramientas. */}
        {/* El selector de agendas (dirección, 6/10/2026): «En la agenda la
            lista se hace muy larga. ¿Cómo podemos reducir el espacio que
            ocupa?». Las píldoras, una por agenda, ocupaban seis filas con 17
            agendas; ahora es UNA línea: un botón que dice lo que se ve y, al
            abrirlo, la lista con casillas (y «Todas» arriba). Mismas reglas
            que las píldoras: `togglePill`, `pulsarTodas` y la selección
            guardada. Cómo se usa, en SelectorAgendas.jsx. */}
        {agendas.length > 1 && (
          <SelectorAgendas
            agendas={agendas}
            // Con «Todas», marcadas las columnas que se ven (como las
            // píldoras encendidas de antes) o, en el mes, las agendas del
            // mes; si no, las elegidas.
            marcadas={vistaTodas ? (view === 'mes' ? idsMes : idsColumnas) : selectedIds}
            vistaTodas={vistaTodas}
            etiquetaTodas={view === 'mes' ? 'Todas (abiertas o con citas)' : undefined}
            pistaTodas={view === 'mes' ? 'solo las abiertas o con citas este mes' : undefined}
            // Sus pacientes del día junto a cada agenda (5/10/2026), se mire
            // o no; en la vista semana, sin número.
            numeroDe={numeroDe}
            numerosAlDia={numerosAlDia}
            tituloDe={(a) => tituloPacientes(a, numeroDe(a), date)}
            // En el botón, el total de lo que se ve: el del contador de
            // pacientes de abajo (personas, cada una una vez).
            totalPacientes={view === 'dia' ? pacientesDia : null}
            diaTexto={diaEnFrase(date)}
            onAlternar={togglePill}
            onTodas={pulsarTodas}
            onSolo={verSolo}
          />
        )}

        {/* Fila de abajo: a la izquierda el día que miras, a la derecha las
            herramientas de ese día. */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Navegación de fecha */}
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={() => moverFecha(-1)}
              aria-label={view === 'mes' ? 'Mes anterior' : 'Anterior'}
              data-testid="citas-date-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              className="h-9"
              onClick={() => setDate(todayStr())}
              data-testid="citas-date-today"
            >
              Hoy
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={() => moverFecha(1)}
              aria-label={view === 'mes' ? 'Mes siguiente' : 'Siguiente'}
              data-testid="citas-date-next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>

            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" className="h-9 font-medium text-[#13231E]" data-testid="citas-date-label">
                  <CalendarDays className="w-4 h-4 mr-2 text-[#2E6E5E]" />
                  {view === 'semana' ? weekLabel(monday) : view === 'mes' ? textoMes(mesVisto) : dayLabel(date)}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  locale={es}
                  selected={strToDate(date)}
                  defaultMonth={strToDate(date)}
                  onSelect={(d) => {
                    if (!d) return;
                    setDate(dateToStr(d));
                    setCalOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Día / Semana / Mes. Con varias agendas (o «Todas») no hay
              semana: Día y Mes (14/10/2026). */}
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="h-9" data-testid="citas-view-toggle">
              <TabsTrigger value="dia" data-testid="citas-view-dia">
                Día
              </TabsTrigger>
              {!allView && (
                <TabsTrigger value="semana" data-testid="citas-view-semana">
                  Semana
                </TabsTrigger>
              )}
              <TabsTrigger value="mes" data-testid="citas-view-mes">
                Mes
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Herramientas del día, todas juntas a la derecha y siempre en el
              mismo orden (2026-07-26): antes iban sueltas en la fila y cambiaban
              de sitio según qué botones tocaran a cada usuario.
              El botón "IA" se quitó: el cuadro de abajo y el Agente IA del menú
              abren ya ese mismo chat. */}
          {/* En móvil, cuadrícula de 2 columnas con botones del mismo ancho:
              empaquetados y sin huecos (2026-08-30, dirección). En pantalla
              grande, fila flexible a la derecha como siempre. */}
          <div className="grid grid-cols-2 gap-1.5 w-full sm:flex sm:flex-wrap sm:items-center sm:w-auto sm:ml-auto">
          {/* Recordatorios de cita (Phase 27.17) — gestión, no para doctores */}
          {!doctor && (
          <Button
            variant="outline"
            className="h-8 px-2 text-xs sm:h-9 sm:px-4 sm:text-sm"
            onClick={() => setRecordatoriosOpen(true)}
            data-testid="citas-recordatorios"
          >
            <BellRing className="w-4 h-4 mr-1.5 text-[#2E6E5E]" /> Recordatorios
          </Button>
          )}

          {/* Agenda del día imprimible.
              OJO con la condición: antes era `agenda && view === 'dia'`, y
              `agenda` vale null en cuanto hay DOS agendas seleccionadas. El
              botón desaparecía sin decir nada — y como la selección se guarda
              en el navegador de cada uno, a unos les salía y a otros no, con
              el mismo permiso (2026-07-28, lo vio administración). Ahora se ve
              siempre que haya alguna agenda, y si hay varias (o estás en
              semana) se pregunta cuál y qué día. */}
          {selectedAgendas.length > 0 && (
            <Button
              variant="outline"
              className="h-8 px-2 text-xs sm:h-9 sm:px-4 sm:text-sm"
              onClick={() => abrirImpresion(null)}
              data-testid="citas-imprimir-dia"
            >
              <Printer className="w-4 h-4 mr-1.5 text-[#2E6E5E]" /> Imprimir
            </Button>
          )}

          {/* Agendas disponibles (administración, 14/10/2026): la página con el
              calendario anual de cada doctor —qué días viene, sus vacaciones,
              días libres y bloqueos— y el formulario de bloquear agendas
              (festivos, congresos, unas horas…). Era «Disponibilidad Dres.»
              (8/10/2026) y se queda también con lo de «Agendas bloqueadas»
              (2026-08-26), cuyo botón desaparece. Desde el 3/11/2026 lo ven
              también los médicos: entran a ver SOLO su propia disponibilidad,
              sin editar (el servidor les da solo lo suyo). */}
          <Button
            variant="outline"
            className="h-8 px-2 text-xs sm:h-9 sm:px-4 sm:text-sm"
            asChild
            data-testid="citas-agendas-disponibles"
          >
            <Link to="/agendas-disponibles">
              <CalendarRange className="w-4 h-4 mr-1.5 text-[#15803d]" /> Agendas disponibles
            </Link>
          </Button>

          {/* Perfil de enfermería: exporta de la agenda del doctor solo las
              primeras valoraciones y primeras revisiones (Phase 27.22). El
              rótulo lo pone cada clínica en su configuración. */}
          {/* El botón «Agenda de enfermería» se jubila (2026-08-20): la agenda de
              Enfermería ya ENSEÑA e IMPRIME su día completo — sus citas más
              las de su perfil en otras consultas. Se abre y se imprime. */}

          {/* Suscripción del doctor a su agenda desde Google/Apple Calendar */}
          {user?.agenda_id && (
            <Button
              variant="outline"
              className="h-8 px-2 text-xs sm:h-9 sm:px-4 sm:text-sm"
              onClick={abrirIcal}
              data-testid="citas-google-calendar"
            >
              <CalendarPlus className="w-4 h-4 mr-1.5 text-[#2E6E5E]" /> Google Calendar
            </Button>
          )}

          {/* Lista de espera retirada de la interfaz (2026-08-29, dirección:
              no se usaba). El backend sigue; para recuperarla, git. */}

          {/* Acciones de admin, al final y detrás de una raya: tocan cómo está
              montada la agenda, no el día que estás mirando. */}
          {admin && agenda && (
            <>
              <span className="hidden sm:block w-px h-6 bg-[#D9E3DD] mx-0.5" aria-hidden="true" />
              <Button
                variant="outline"
                className="h-8 px-2 text-xs sm:h-9 sm:px-4 sm:text-sm"
                onClick={() => setBloqueoOpen(true)}
                data-testid="citas-new-bloqueo"
              >
                <Lock className="w-4 h-4 mr-1.5" /> Bloqueo
              </Button>
              <Button
                variant="outline"
                className="h-8 px-2 text-xs sm:h-9 sm:w-9 sm:p-0 sm:text-sm"
                onClick={() => setConfigOpen(true)}
                aria-label="Configurar agenda"
                data-testid="citas-config-agenda"
              >
                <Settings className="w-4 h-4" />
                <span className="ml-1.5 sm:hidden">Configurar</span>
              </Button>
              <Button variant="outline" className="h-8 px-2 text-xs sm:h-9 sm:px-4 sm:text-sm" asChild data-testid="citas-gestion-agendas-link">
                <Link to="/gestion-agendas">Gestión de agendas</Link>
              </Button>
            </>
          )}
          </div>
        </div>
      </div>

      {/* Cuadro de IA en una sola línea (2026-08-30, dirección: solo «Agente IA»
          + escribir o dictar; el texto explicativo sobraba). Solo a quien
          tiene el Agente (D7, 22/9/2026): al doctor no se le ofrece. */}
      {agente && (
      <div className="border border-[#2E6E5E]/40 bg-[#2E6E5E]/[0.04] rounded-lg p-2 mb-3 flex items-center gap-2"
        data-testid="citas-ia-cuadro">
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#13231E] shrink-0 pl-1">
          <AgenteIcono size={24} />
          Agente IA
        </span>
        <Input
          value={iaCuadro}
          onChange={(e) => setIaCuadro(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') lanzarIaCuadro(); }}
          placeholder="Pídele aquí la cita…"
          className="h-9 flex-1 min-w-0 bg-white dark:bg-card"
          data-testid="citas-ia-cuadro-input"
        />
        <MicButton onText={(t) => setIaCuadro((prev) => (prev ? `${prev} ${t}` : t))} />
        <Button onClick={lanzarIaCuadro} disabled={!iaCuadro.trim()}
          className="h-9 bg-[#2E6E5E] hover:bg-[#1E4A3F] shrink-0"
          title="Pedírselo a la IA de la agenda"
          data-testid="citas-ia-cuadro-enviar">
          <AgenteIcono size={20} />
        </Button>
      </div>
      )}

      {/* Contador de pacientes del día (encima de la agenda). En la vista
          «Mes» no: cada día del mes lleva los suyos (14/10/2026). */}
      {view !== 'mes' && (
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="citas-contador-pacientes">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(46,110,94,0.10)] border border-[#2E6E5E]/25 px-3 py-1 text-sm">
          <Users className="w-4 h-4 text-[#2E6E5E]" />
          <strong className="text-[#13231E] tabular-nums">{pacientesDia}</strong>
          <span className="text-[#13231E]/80">paciente{pacientesDia === 1 ? '' : 's'} este día</span>
        </span>
        {multiView && (
          <span className="text-xs text-muted-foreground">
            {/* Con «Todas», las que se ven (5/10/2026). */}
            en {agendasEnContador} agenda{agendasEnContador === 1 ? '' : 's'}
          </span>
        )}
        {/* «Todas» (5/10/2026): lo que ha cambiado un compañero no mueve las
            columnas solo; se avisa aquí y se pone al día con un clic. */}
        {cambiosEnTodas && (
          <button
            type="button"
            onClick={ponerAlDiaTodas}
            className="text-xs font-medium text-[#2E6E5E] hover:underline text-left"
            data-testid="citas-todas-cambios"
          >
            {textoCambiosEnTodas} ▸ Poner al día
          </button>
        )}
      </div>
      )}

      {/* Contenido */}
      {loadingBase ? (
        <div className="space-y-3">
          <Skeleton className="h-9 w-72 rounded-md" />
          <Skeleton className="h-[420px] w-full rounded-xl" />
        </div>
      ) : agendas.length === 0 ? (
        <EmptyState
          icon={CalendarOff}
          title="No hay agendas disponibles"
          description="Todavía no se ha configurado ninguna agenda de citas. Contacta con administración."
        />
      ) : view === 'mes' ? (
        /* La vista «Mes» (14/10/2026): una casilla por día con la mañana y la
           tarde de cada agenda. Con «Todas» y ninguna agenda abierta ni con
           citas en todo el mes, se dice con palabras. */
        <div className={cargaLenta ? 'opacity-60 pointer-events-none transition-opacity' : 'transition-opacity'}>
          {errorMes && (
            <div
              role="alert"
              className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-[#D9E3DD] bg-white px-3 py-2 text-sm text-[#13231E]"
              data-testid="citas-mes-error"
            >
              <span>No se pudo cargar el mes.</span>
              <Button variant="outline" className="h-8 px-3" onClick={() => cargarMes()}
                data-testid="citas-mes-reintentar">
                Reintentar
              </Button>
            </div>
          )}
          {vistaTodas && datosMes && agendasMes.length === 0 ? (
            <div data-testid="citas-mes-todas-vacio">
              <EmptyState
                icon={CalendarOff}
                title={`Ninguna agenda está abierta ni tiene citas en ${textoMes(mesVisto).toLowerCase()}`}
                description="Para ver una agenda concreta, márcala en el selector de agendas de arriba."
              />
            </div>
          ) : (
            <AgendaMes
              mes={mesVisto}
              agendas={agendasMes}
              datos={datosMes}
              error={errorMes}
              hoy={todayStr()}
              movil={esMovilMulti}
              // Los enlaces del aviso de las agendas que abren de lunes a
              // viernes sin revisar (15/10/2026), a quien puede configurar
              // agendas: el mismo permiso que «Configurar agenda» de arriba.
              puedeConfigurar={admin}
              onAbrirDia={abrirDiaDelMes}
            />
          )}
        </div>
      ) : showDayEmpty ? (
        <div className="space-y-2">
          {/* El equipo no médico también los días sin rejilla (revisión del
              14/10/2026): encima del aviso, una vez. */}
          <LeyendaEquipo fechas={[date]} refreshKey={reloadKey} />
          <EmptyState
            icon={CalendarOff}
            title="Sin consulta este día"
            description={`La agenda ${agenda.name} no tiene horario configurado para el ${dayLabel(date).toLowerCase()}.`}
            action={
              <Button variant="outline" onClick={() => setDate(todayStr())}>
                Ir a hoy
              </Button>
            }
          />
        </div>
      ) : showWeekEmpty ? (
        <div className="space-y-2">
          <LeyendaEquipo fechas={weekDates} refreshKey={reloadKey} />
          <EmptyState
            icon={CalendarOff}
            title="Sin consulta esta semana"
            description={`La agenda ${agenda.name} no tiene horario configurado entre semana para estas fechas.`}
            action={
              <Button variant="outline" onClick={() => setDate(todayStr())}>
                Ir a hoy
              </Button>
            }
          />
        </div>
      ) : vistaTodas && columnasMulti.length === 0 ? (
        /* «Todas» sin ninguna agenda con pacientes ese día (5/10/2026): se
           dice con palabras y cómo dar cita en una vacía. Mientras llega el
           día (primera carga, o al pasar de un día vacío a otro), el hueco
           de la agenda en vez de este aviso, que sería del día anterior. */
        ordenDelDia.fecha === date ? (
          <div data-testid="citas-todas-vacia">
            <LeyendaEquipo fechas={[date]} refreshKey={reloadKey} className="mb-2" />
            <EmptyState
              icon={CalendarOff}
              title={`Ninguna agenda tiene pacientes citados el ${diaEnFrase(date)}`}
              description={`${estadoDia.fecha === date && estadoDia.porAgenda
                ? 'Tampoco hay doctores disponibles ni agendas bloqueadas ese día. ' : ''}`
                + 'Para dar una cita en una agenda vacía, márcala en el selector de agendas de arriba: se abre y puedes pulsar un hueco.'}
              action={date !== todayStr() ? (
                <Button variant="outline" onClick={() => setDate(todayStr())}>
                  Ir a hoy
                </Button>
              ) : null}
            />
          </div>
        ) : (
          <Skeleton className="h-[420px] w-full rounded-xl" />
        )
      ) : allView ? (
        /* Varias agendas a la vez (vista día): una columna por agenda seleccionada.
           Todas a la vista sin scroll horizontal (administración, 5/10/2026:
           «tienen que poder verse todas a la vez en pantalla»). Antes cada
           columna pedía 280 px como mínimo y, con varias, las últimas se
           quedaban fuera, a la derecha. Ahora se reparten el ancho que haya
           (flex-1 + min-w-0, sin mínimo) y, si salen estrechas, dejan la regla
           de horas y juntan un poco la separación. */
        <div className={cargaLenta ? 'opacity-60 pointer-events-none transition-opacity' : 'transition-opacity'}>
          {/* Quién del equipo no médico está de vacaciones, teletrabajando o
              de día libre (administración, 14/10/2026): UNA vez, encima de
              las columnas; el visitante va en la cabecera de cada una. Ver
              components/citas/LeyendaEquipo.jsx. */}
          <LeyendaEquipo fechas={[date]} refreshKey={reloadKey} className="mb-2" />
          <div
            ref={setMultiEl}
            className={`flex min-w-0 pb-2 ${esMovilMulti ? 'overflow-x-auto' : ''}`}
            style={{ gap: multiEstrecha ? HUECO_COLUMNAS_ESTRECHAS : HUECO_COLUMNAS }}
            data-testid="citas-todas-grid"
            data-estrecha={multiEstrecha ? '1' : undefined}
          >
            {columnasMulti.map((a) => (
              <div key={a.id} className="flex-1 min-w-0" data-testid={`citas-todas-col-${a.id}`}
                style={esMovilMulti ? { minWidth: ANCHO_COLUMNA_MOVIL } : undefined}>
                {/* El nombre de la agenda: si la columna es estrecha, en dos
                    líneas de letra más pequeña (con diez agendas, en una sola
                    todas se quedaban en «Consul…»), partiendo con guion la
                    palabra que no quepa. Mismo alto en todas las columnas,
                    para que las rejillas empiecen a la par. */}
                <div
                  className={`flex gap-1.5 mb-1 px-1 min-w-0 ${multiEstrecha ? 'items-start min-h-[2rem]' : 'items-center'}`}
                  title={a.name}
                >
                  <span
                    className={`w-2.5 h-2.5 rounded-full inline-block shrink-0 ${multiEstrecha ? 'mt-[3px]' : ''}`}
                    style={{ backgroundColor: a.color || '#2E6E5E' }}
                  />
                  <span
                    className={`min-w-0 font-semibold text-[#13231E] ${multiEstrecha
                      ? 'text-xs leading-tight line-clamp-2 break-words hyphens-auto'
                      : 'text-sm truncate'}`}
                  >{a.name}</span>
                  {/* Sus pacientes del día, como en su píldora (5/10/2026). */}
                  {numeroDe(a) != null && (
                    <NumeroPacientes
                      n={numeroDe(a)}
                      alDia={numerosAlDia}
                      title={tituloPacientes(a, numeroDe(a), date)}
                      testId={`citas-pacientes-col-${a.id}`}
                    />
                  )}
                </div>
                {/* Quién viene de visita ese día (administración, 7/10/2026),
                    bajo el nombre: «Visitante: Dra. Ejemplo · ORL». Solo
                    lectura; se apunta en la agenda suelta. */}
                {hayVisitantesEnColumnas && (visitantesDe(a).length ? (
                  <VisitantesColumna medicos={visitantesDe(a)} testId={`citas-visitantes-col-${a.id}`} />
                ) : (
                  <div className="h-4 mb-1" aria-hidden="true" data-testid={`citas-visitantes-hueco-${a.id}`} />
                ))}
                {/* Con «Todas» (14/10/2026): «Disponible» en verde, o
                    «Vacaciones», «Día libre», «Bloqueada: motivo» en rojo;
                    con el formato del visitante. «Vacaciones (falta
                    bloquear esta agenda)» si el doctor lo está pero esta
                    agenda aún no tiene su bloqueo. */}
                {hayEstadosEnColumnas && (estadoDe(a) ? (
                  <EstadoAgendaColumna estado={estadoDe(a).estado} motivo={estadoDe(a).motivo}
                    faltaBloqueo={!!estadoDe(a).falta_bloqueo} horas={estadoDe(a).horas || ''}
                    testId={`citas-estado-col-${a.id}`} />
                ) : (
                  <div className="h-4 mb-1" aria-hidden="true" data-testid={`citas-estado-hueco-${a.id}`} />
                ))}
                <AgendaGrid
                  agenda={a}
                  date={date}
                  // Las suyas y, en la de enfermería, también las de su perfil
                  // en otras consultas: lo mismo que cuenta para el orden.
                  citas={citasDeLaColumna(a, citas)}
                  bloqueos={bloqueosPorAgenda[a.id] || []}
                  conceptos={conceptos}
                  agendas={agendas}
                  onSlotClick={({ date: d, start }) => setCreateSlot({ date: d, start, agendaId: a.id })}
                  onCitaClick={onCitaClick}
                  isAdmin={puedeForzarHueco}
                  compact
                  ajustarAlAncho={!esMovilMulti}
                  sinRegla={multiEstrecha}
                />
              </div>
            ))}
          </div>
        </div>
      ) : agenda ? (
        <div className={cargaLenta ? 'opacity-60 pointer-events-none transition-opacity' : 'transition-opacity'}>
          {view === 'dia' ? (
            <div className="space-y-2">
              <QuirofanoDiaBar
                agenda={agenda}
                date={date}
                refreshKey={reloadKey}
                onChanged={reload}
              />
              {/* Médico visitante en las agendas de consulta (administración,
                  6/10/2026): en quirófano va dentro de la barra de arriba; en
                  las demás, una barra mínima solo con él. */}
              <MedicoVisitanteBarra agenda={agenda} date={date} refreshKey={reloadKey} />
              {/* Y debajo, el equipo no médico de vacaciones, teletrabajando
                  o de día libre ese día (administración, 14/10/2026). */}
              <LeyendaEquipo fechas={[date]} refreshKey={reloadKey} />
              <AgendaGrid
                agenda={agenda}
                date={date}
                citas={citas}
                bloqueos={bloqueos}
                conceptos={conceptos}
                agendas={agendas}
                onSlotClick={onSlotClick}
                onCitaClick={onCitaClick}
                isAdmin={puedeForzarHueco}
              />
            </div>
          ) : (
            <div className="space-y-2">
              {/* El equipo no médico de la semana, una línea por día que tenga
                  a alguien (administración, 14/10/2026). */}
              <LeyendaEquipo fechas={weekDates} refreshKey={reloadKey} />
              <AgendaGrid
                agenda={agenda}
                dates={weekDates}
                citas={citas}
                bloqueos={bloqueos}
                conceptos={conceptos}
                agendas={agendas}
                onSlotClick={onSlotClick}
                onCitaClick={onCitaClick}
                isAdmin={puedeForzarHueco}
                compact
                showHeaders
                onDateHeaderClick={(d) => {
                  setDate(d);
                  setView('dia');
                }}
              />
            </div>
          )}
        </div>
      ) : null}

      {/* Diálogos. En la vista "Todas" la agenda del diálogo se resuelve por
          la columna pulsada (crear) o por la propia cita (detalle). */}
      <CitaCreateDialog
        open={!!createSlot}
        onOpenChange={(v) => {
          if (!v) setCreateSlot(null);
        }}
        agenda={allView ? agendas.find((a) => a.id === createSlot?.agendaId) || null : agenda}
        defaultDate={createSlot?.date}
        defaultStart={createSlot?.start}
        onCreated={reload}
      />

      <CitaDetailPopover
        open={detailOpen}
        onOpenChange={setDetailOpen}
        cita={citaViva}
        conceptos={conceptos}
        agendas={agendas}
        agenda={allView ? agendas.find((a) => a.id === citaViva?.agenda_id) || null : agenda}
        onChanged={onCitaActualizada}
      />

      {/* El Agente IA, el mismo del menú y del buscador. Es de
          administración, dirección y enfermería (D7, 22/9/2026): el servidor
          (auth.require_agente) le contesta 403 al doctor y al anestesista en
          /api/agente/preguntar, así que a ellos ni se les monta. Dentro, las
          herramientas siguen filtrando por los permisos de cada uno. */}
      {agente && (
        <AgenteAskDialog open={iaOpen} onOpenChange={(v) => { setIaOpen(v); if (!v) setIaQuery(''); }}
          initialQuery={iaQuery} onCitaCambiada={reload} />
      )}

      {/* Recordatorios y Espera: sus botones los ve todo el personal menos los
          doctores, así que los diálogos se montan con la MISMA condición. Antes
          estaban dentro del bloque de admin y para el resto el botón no hacía
          nada al pulsarlo. */}
      {!doctor && (
        <>
          {/* Con el día y la agenda que se están viendo (2026-09-03, dirección,
              punto 3): el panel manda a «todas las citas de la agenda que
              esté seleccionada en ese momento». Con varias a la vez, el
              panel arranca en la primera y deja cambiar. */}
          <RecordatoriosDialog
            open={recordatoriosOpen}
            onOpenChange={setRecordatoriosOpen}
            onChanged={reload}
            agendas={selectedAgendas}
            date={date}
          />
        </>
      )}

      {admin && (
        <>
          <BloqueoDialog
            open={bloqueoOpen}
            onOpenChange={setBloqueoOpen}
            agenda={agenda}
            defaultDate={date}
            onSaved={reload}
          />
          <AgendaConfigDialog
            open={configOpen}
            onOpenChange={setConfigOpen}
            agenda={agenda}
            conceptos={conceptosElegibles(conceptos)}
            onSaved={(updated) => {
              if (updated?.id) {
                setAgendas((prev) => prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));
              }
              // El orden de las píldoras lo pone el servidor, por profesional
              // (administración, 5/10/2026): cambiarle el profesional a la
              // agenda la cambia de sitio, así que la lista se vuelve a pedir
              // también cuando ya se ha puesto al día aquí.
              listAgendas().then((ags) => { if (Array.isArray(ags)) setAgendas(ags); }).catch(() => {});
              reload();
            }}
          />
        </>
      )}

      <PdfPreviewDialog
        open={!!pdfPreview}
        onOpenChange={(o) => !o && setPdfPreview(null)}
        url={pdfPreview?.url}
        filename={pdfPreview?.filename}
        title={pdfPreview?.title}
      />

      {/* Qué agenda y qué día imprimir, cuando no hay una sola respuesta obvia. */}
      <Dialog open={!!imprimirPicker} onOpenChange={(o) => !o && setImprimirPicker(null)}>
        <DialogContent className="max-w-sm" data-testid="citas-imprimir-picker">
          <DialogHeader>
            <DialogTitle>
              {imprimirPicker?.perfil ? clinicaDatos.agenda_perfil_enfermeria : 'Imprimir el parte del día'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Agenda</label>
              <select
                className="w-full mt-1 h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={imprimirPicker?.agendaId || ''}
                onChange={(e) => setImprimirPicker((p) => ({ ...p, agendaId: e.target.value }))}
                data-testid="citas-imprimir-agenda"
              >
                {agendasParaImprimir.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Día</label>
              <Input
                type="date"
                className="mt-1 h-9"
                value={imprimirPicker?.dia || date}
                onChange={(e) => setImprimirPicker((p) => ({ ...p, dia: e.target.value }))}
                data-testid="citas-imprimir-dia-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setImprimirPicker(null)}>Cancelar</Button>
            <Button
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              disabled={!imprimirPicker?.agendaId || !imprimirPicker?.dia}
              onClick={() => {
                const ag = selectedAgendas.find((a) => a.id === imprimirPicker.agendaId);
                if (!ag) return;
                pdfDelDia(ag, imprimirPicker.dia, imprimirPicker.perfil);
                setImprimirPicker(null);
              }}
              data-testid="citas-imprimir-confirmar"
            >
              <Printer className="w-4 h-4 mr-1.5" /> Ver el parte
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Enlace de suscripción a Google/Apple Calendar */}
      <Dialog open={icalOpen} onOpenChange={setIcalOpen}>
        <DialogContent className="max-w-lg" data-testid="citas-ical-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#13231E]">
              <CalendarPlus className="w-5 h-5 text-[#2E6E5E]" /> Tu agenda en Google Calendar
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[#13231E]">
            <p className="text-muted-foreground">
              Añade tu agenda a Google Calendar y se mantiene al día sola. Por privacidad,
              solo se envía el <strong className="text-[#13231E]">nombre del paciente y las notas</strong> —
              ningún otro dato. No compartas el enlace: es personal.
            </p>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={icalUrl || 'Generando enlace…'}
                onFocus={(e) => e.target.select()}
                className="text-xs"
                data-testid="citas-ical-url"
              />
              <Button variant="outline" size="icon" className="shrink-0" onClick={copiarIcal} disabled={!icalUrl}>
                {icalCopied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                disabled={!icalUrl || icalBusy}
                onClick={anadirIcal}
                data-testid="citas-ical-google"
              >
                <CalendarPlus className="w-4 h-4 mr-1.5" /> Añadir a Google Calendar
              </Button>
              <Button
                variant="outline"
                className="text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800"
                disabled={!icalUrl || icalBusy}
                onClick={quitarIcal}
                data-testid="citas-ical-remove"
              >
                <CalendarOff className="w-4 h-4 mr-1.5" /> Quitar de Google Calendar
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              «Quitar» vacía tu agenda en Google (se mantiene el enlace por si la vuelves a añadir).
              Google sincroniza cada pocas horas, así que los cambios —añadir o quitar— pueden tardar
              un poco en verse. En móvil o Apple Calendar: copia el enlace y usa «Añadir/Suscribir calendario desde URL».
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
